import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Pencil, Wallet, Banknote, Landmark, Plus, Minus, ArrowLeftRight, Percent, Ship, LifeBuoy, History } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y}`;
};
const monthShort = (m) => {
  if (!m) return '';
  const [, mo] = m.split('-');
  return MONTH_NAMES[parseInt(mo, 10) - 1].toUpperCase();
};

// Big bold green KPI box (Google Sheets style)
function KpiBox({ label, value, onEdit, testId, tone = 'money' }) {
  const toneClasses = {
    money: 'bg-money text-white border-money',
    blue: 'bg-sheet text-white border-sheet',
    primary: 'bg-primary text-primary-foreground border-primary',
  };
  return (
    <div
      className="flex items-stretch border-2 border-foreground/10 rounded-md overflow-hidden shadow-sm"
      data-testid={testId}
    >
      <div className={`${toneClasses[tone]} px-5 py-4 flex items-center font-display text-base sm:text-lg tracking-widest min-w-[160px] text-center`}>
        {label.toUpperCase()}
      </div>
      <div className="flex-1 bg-card px-5 py-4 flex items-center justify-between gap-3">
        <span className="font-display text-2xl md:text-3xl text-foreground tabular-nums">
          ${Number(value || 0).toLocaleString()}
        </span>
        {onEdit && (
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onEdit} data-testid="edit-budget-btn">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [cashInput, setCashInput] = useState('');
  const [bankInput, setBankInput] = useState('');
  const [taxInput, setTaxInput] = useState('');
  const [taxSavingsInput, setTaxSavingsInput] = useState('');
  const [emergencyInput, setEmergencyInput] = useState('');
  const [savingsOpen, setSavingsOpen] = useState(false);
  const [savingsAccount, setSavingsAccount] = useState('tax');
  const [adjType, setAdjType] = useState('add');
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveType, setMoveType] = useState('deposit');
  const [moveAmount, setMoveAmount] = useState('');
  const [moveBucket, setMoveBucket] = useState('cash');

  const fetchStats = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/savings`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setFin(f.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openBudget = () => {
    setCashInput(String(stats?.cash_on_hand ?? ''));
    setBankInput(String(stats?.bank_balance ?? ''));
    setTaxInput(String(stats?.tax_rate ?? ''));
    setTaxSavingsInput(String(stats?.tax_savings_opening ?? ''));
    setEmergencyInput(String(stats?.emergency_savings_opening ?? ''));
    setBudgetOpen(true);
  };

  const handleBudgetSave = async () => {
    try {
      await axios.put(`${API}/budget`, {
        cash_on_hand: parseFloat(cashInput) || 0,
        bank_balance: parseFloat(bankInput) || 0,
        tax_rate: parseFloat(taxInput) || 0,
        tax_savings_opening: parseFloat(taxSavingsInput) || 0,
        emergency_savings_opening: parseFloat(emergencyInput) || 0,
      }, { withCredentials: true });
      toast.success('Capital updated');
      setBudgetOpen(false);
      fetchStats();
    } catch (e) {
      toast.error('Failed to update capital');
    }
  };

  const openMove = (type) => {
    setMoveType(type);
    setMoveAmount('');
    setMoveBucket('cash');
    setMoveOpen(true);
  };

  const handleMove = async () => {
    const amt = parseFloat(moveAmount);
    if (!amt || amt <= 0) { toast.error('Enter an amount greater than 0'); return; }
    try {
      if (moveType === 'transfer') {
        await axios.post(`${API}/budget/transfer`, {
          source: moveBucket,
          dest: moveBucket === 'cash' ? 'bank' : 'cash',
          amount: amt,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API}/budget/adjust`, {
          account: moveBucket,
          direction: moveType,
          amount: amt,
        }, { withCredentials: true });
      }
      toast.success(moveType === 'deposit' ? 'Deposit added' : moveType === 'withdraw' ? 'Withdrawal recorded' : 'Transfer complete');
      setMoveOpen(false);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update balances');
    }
  };

  const openSavings = (account) => {
    setSavingsAccount(account);
    setAdjType('add');
    setAdjAmount('');
    setAdjReason('');
    setSavingsOpen(true);
  };

  const handleSavingsAdjust = async () => {
    const amt = parseFloat(adjAmount);
    if (isNaN(amt)) { toast.error('Enter an amount'); return; }
    if (adjType !== 'set' && amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    try {
      await axios.post(`${API}/savings/adjust`, {
        account: savingsAccount, type: adjType, amount: amt, reason: adjReason,
      }, { withCredentials: true });
      toast.success('Balance adjusted');
      setSavingsOpen(false);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to adjust balance');
    }
  };

  const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtCtDate = (s) => {
    if (!s) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    try { return new Date(s).toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }); }
    catch (e) { return String(s).slice(0, 10); }
  };

  const renderSavings = (account, title, d, tone, Icon) => (
    <div className="rounded-lg border border-foreground/10 p-4" data-testid={`savings-${account}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
          <Icon className={`h-3.5 w-3.5 ${tone}`} /> {title} — Tracked Balance
        </span>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSavings(account)} data-testid={`edit-balance-${account}`}>Edit Balance</Button>
      </div>
      <div className={`font-display text-3xl tabular-nums ${tone}`} data-testid={`savings-balance-${account}`}>{money(d?.balance)}</div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
        <div><div className="text-muted-foreground">Opening</div><div className="font-semibold tabular-nums">{money(d?.opening)}</div></div>
        <div><div className="text-muted-foreground">Completed</div><div className="font-semibold tabular-nums text-emerald-600">+{money(d?.completed)}</div></div>
        <div><div className="text-muted-foreground">Adjustments</div><div className="font-semibold tabular-nums">{(d?.adjustments_total || 0) >= 0 ? '+' : ''}{money(d?.adjustments_total)}</div></div>
      </div>
      <div className="text-[11px] text-muted-foreground mt-2">
        Planned (not yet added): <span className="font-semibold text-amber-600 tabular-nums">{money(d?.planned)}</span>
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" /></div>;
  if (!stats) return <div className="text-center py-20 text-muted-foreground">Failed to load dashboard.</div>;

  const chartData = [...(stats.monthly_sales || [])].reverse().slice(-12).map(r => ({ ...r, month: formatMonth(r.month) }));

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Big centered title (Google Sheets vibe) */}
      <div className="text-center pb-2 border-b-2 border-foreground/15">
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-foreground tracking-wide">
          BUDGET
        </h1>
      </div>

      {/* Three large colored budget cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="primary-cards">
        <div>
          <KpiBox label="Available Money" value={stats.cash_bank_total ?? stats.available_cash} onEdit={openBudget} testId="card-available-money" tone="money" />
          <div className="mt-1.5 text-[11px] text-muted-foreground pl-1" data-testid="available-for-purchases">
            Available for phone purchases: <span className="font-semibold text-emerald-600 tabular-nums">{money(stats.available_for_purchases ?? stats.available_cash)}</span>
          </div>
        </div>
        <KpiBox label="Money in Phones" value={stats.inventory_value} testId="card-money-in-phones" tone="blue" />
        <KpiBox label="Overall Money" value={stats.overall_money} testId="card-overall-money" tone="primary" />
      </div>

      {/* Cash / Bank / Tax info + capital action buttons */}
      <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="capital-actions">
        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap" data-testid="capital-breakdown">
          <span className="flex items-center gap-1.5"><Banknote className="h-4 w-4 text-money" /> Cash: <span className="font-semibold text-foreground tabular-nums">{money(stats.cash_on_hand)}</span></span>
          <span className="flex items-center gap-1.5"><Landmark className="h-4 w-4 text-sheet" /> Bank: <span className="font-semibold text-foreground tabular-nums">{money(stats.bank_balance)}</span></span>
          <span className="flex items-center gap-1.5" data-testid="tax-rate-chip"><Percent className="h-4 w-4 text-primary" /> Tax: <span className="font-semibold text-foreground tabular-nums">{Number(stats.tax_rate || 0)}%</span></span>
          {stats.unpaid_sales > 0 && (
            <span className="text-amber-600" data-testid="unpaid-indicator">Unpaid sales: {money(stats.unpaid_sales)}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => openMove('deposit')} data-testid="deposit-btn">
            <Plus className="h-4 w-4 mr-1.5 text-money" /> Deposit
          </Button>
          <Button variant="outline" size="sm" onClick={() => openMove('withdraw')} data-testid="withdraw-btn">
            <Minus className="h-4 w-4 mr-1.5 text-destructive" /> Withdraw
          </Button>
          <Button variant="outline" size="sm" onClick={() => openMove('transfer')} data-testid="transfer-btn">
            <ArrowLeftRight className="h-4 w-4 mr-1.5 text-sheet" /> Transfer
          </Button>
          <Button variant="outline" size="sm" onClick={openBudget} data-testid="set-capital-btn">
            <Wallet className="h-4 w-4 mr-1.5" /> Set
          </Button>
        </div>
      </div>

      {/* Four secondary statistic cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="secondary-cards">
        {[
          { label: 'Inventory Value', value: money(stats.inventory_value), testId: 'stat-inventory-value' },
          { label: 'Projected Revenue', value: money(stats.projected_revenue), testId: 'stat-projected-revenue' },
          { label: 'Projected Profit', value: money(stats.projected_profit), testId: 'stat-projected-profit' },
          { label: 'Phones in Stock', value: String(stats.phones_in_stock || 0), testId: 'stat-phones-in-stock' },
        ].map(s => (
          <Card key={s.label} className="border border-foreground/10" data-testid={s.testId}>
            <CardContent className="p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">{s.label}</div>
              <div className="font-display text-2xl text-foreground tabular-nums">{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-xs text-muted-foreground" data-testid="profit-date-basis">Realized profit is attributed to the purchase date. Revenue and sold counts use the actual sale date. Sales without a purchase date use the sale date.</p>

      {/* Tabs: Profit Chart / Weekly Profit / Monthly Sales */}
      <Tabs defaultValue="chart" className="space-y-4">
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="chart" data-testid="tab-chart">Profit Chart</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly-profit">Weekly Profit</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly-sales">Monthly Sales</TabsTrigger>
        </TabsList>

        <TabsContent value="chart">
          <Card className="overflow-hidden border-2 border-foreground/10" data-testid="profit-chart">
            <div className="bg-sheet text-white px-5 py-3 font-display tracking-widest text-lg">MONTHLY PROFIT</div>
            <CardContent className="pt-6">
              {chartData.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">No data to chart yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart data={chartData} margin={{ top: 30, right: 20, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={v => `$${v}`} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }} formatter={v => [`$${v.toLocaleString()}`, 'Profit']} />
                    <Bar dataKey="profit" fill="hsl(var(--header-blue))" radius={[4, 4, 0, 0]} name="Profit">
                      <LabelList
                        dataKey="profit"
                        position="top"
                        formatter={(v) => `$${Number(v).toLocaleString()}`}
                        style={{ fill: 'hsl(var(--foreground))', fontWeight: 700, fontSize: 12 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="weekly">
          <Card className="overflow-hidden border-2 border-foreground/10">
            <div className="bg-sheet text-white px-5 py-3 font-display tracking-widest text-lg">WEEKLY PROFIT BREAKDOWN</div>
            {(stats.weekly_profit || []).length === 0 ? (
              <p className="text-muted-foreground text-center py-12">No weekly data yet. Sell some phones!</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-sheet-soft hover:bg-sheet-soft">
                      <TableHead className="font-bold text-foreground">Month</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Full Month</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Week 1</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Week 2</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Week 3</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Week 4</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(stats.weekly_profit || []).map(row => (
                      <TableRow key={row.month} className="border-b border-foreground/5">
                        <TableCell className="p-0">
                          <div className="bg-label text-foreground font-display text-lg tracking-widest px-4 py-2 text-center mx-1 my-1 rounded">
                            {monthShort(row.month)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-display text-base tabular-nums ${row.full_month >= 0 ? 'text-money' : 'text-destructive'}`}>
                            ${row.full_month?.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">${row.week_1?.toLocaleString() || 0}</TableCell>
                        <TableCell className="text-right tabular-nums">${row.week_2?.toLocaleString() || 0}</TableCell>
                        <TableCell className="text-right tabular-nums">${row.week_3?.toLocaleString() || 0}</TableCell>
                        <TableCell className="text-right tabular-nums">${row.week_4?.toLocaleString() || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="monthly">
          <Card className="overflow-hidden border-2 border-foreground/10">
            <div className="bg-sheet text-white px-5 py-3 font-display tracking-widest text-lg">MONTHLY SALES</div>
            {(stats.monthly_sales || []).length === 0 ? (
              <p className="text-muted-foreground text-center py-12">No monthly data yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-sheet-soft hover:bg-sheet-soft">
                      <TableHead className="font-bold text-foreground">Month</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Inventory Purchased</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Repair Cost</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Inventory Sold</TableHead>
                      <TableHead className="text-right font-bold text-foreground">Profit</TableHead>
                      <TableHead className="text-right font-bold text-foreground"># Bought</TableHead>
                      <TableHead className="text-right font-bold text-foreground"># Sold</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(stats.monthly_sales || []).map(row => (
                      <TableRow key={row.month} className="border-b border-foreground/5">
                        <TableCell className="p-0">
                          <div className="bg-label text-foreground font-display text-lg tracking-widest px-4 py-2 text-center mx-1 my-1 rounded">
                            {monthShort(row.month)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">${row.inventory_purchased?.toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums">${row.repair_cost?.toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums">${row.inventory_sold?.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-display text-base tabular-nums ${row.profit >= 0 ? 'text-money' : 'text-destructive'}`}>
                            ${row.profit?.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{row.num_bought}</TableCell>
                        <TableCell className="text-right tabular-nums">{row.num_sold}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Profit Distribution summary */}
      {fin && (
        <Card className="overflow-hidden border-2 border-foreground/10" data-testid="distribution-summary">
          <div className="bg-sheet text-white px-5 py-3 font-display tracking-widest text-lg flex items-center gap-2">
            <Ship className="h-4 w-4" /> PROFIT DISTRIBUTION
          </div>
          <CardContent className="pt-6 space-y-5">
            {/* Tracked savings accounts */}
            <div className="grid md:grid-cols-2 gap-4">
              {renderSavings('tax', 'Tax Reserve', fin.tax, 'text-amber-600', Landmark)}
              {renderSavings('emergency', 'Emergency Savings', fin.emergency, 'text-emerald-600', LifeBuoy)}
            </div>

            {/* Allocation summaries (informational, not wallets) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-foreground/10 pt-4">
              <div data-testid="dist-reinvestment">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">Reinvestment</div>
                <div className="font-display text-2xl text-sky-600 tabular-nums">{money(fin.reinvestment?.completed)}</div>
                <div className="text-[11px] text-muted-foreground">Planned {money(fin.reinvestment?.planned)}</div>
              </div>
              <div data-testid="dist-personal">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">Personal Spending</div>
                <div className="font-display text-2xl text-rose-600 tabular-nums">{money(fin.personal?.completed)}</div>
                <div className="text-[11px] text-muted-foreground">Planned {money(fin.personal?.planned)}</div>
              </div>
              <div data-testid="dist-total">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">Completed Profit</div>
                <div className="font-display text-2xl text-foreground tabular-nums">{money(fin.overall?.completed_net)}</div>
                <div className="text-[11px] text-muted-foreground">{fin.overall?.count || 0} shipment(s)</div>
              </div>
              <div data-testid="dist-remaining-planned">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">Planned Profit</div>
                <div className="font-display text-2xl text-amber-600 tabular-nums">{money(fin.overall?.planned_net)}</div>
                <div className="text-[11px] text-muted-foreground">not yet transferred</div>
              </div>
            </div>

            {/* Simple shipment history */}
            {fin.recent?.length > 0 && (
              <div className="border-t border-foreground/10 pt-4" data-testid="shipment-history">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">Recent Shipments</div>
                <div className="space-y-1">
                  {fin.recent.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm py-1 border-b border-foreground/5 last:border-0" data-testid={`history-${r.id}`}>
                      <span className="truncate flex-1">{r.label} <span className="text-muted-foreground text-xs">· {fmtCtDate(r.date)}</span></span>
                      <span className="tabular-nums font-semibold mr-3">{money(r.net_profit)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'completed' ? 'bg-emerald-600 text-white' : 'border border-amber-500/40 text-amber-600'}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              <strong>Tracked balances</strong> show money saved (opening + completed allocations + your manual adjustments); <strong>Planned</strong> amounts are from shipments not yet completed. Reinvestment &amp; Personal are informational allocation summaries only. None of these change your actual cash/bank, create revenue, or reduce business profit. The 50% tax split is a savings allocation, <strong>not</strong> your tax rate.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Budget Edit Dialog */}
      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent data-testid="budget-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-wider">UPDATE CAPITAL</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Enter how much capital you have left. Your <span className="font-semibold text-foreground">Available Money</span> is the total of both.
            </p>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Banknote className="h-4 w-4 text-money" /> Cash on hand ($)</Label>
              <Input type="number" value={cashInput} onChange={e => setCashInput(e.target.value)} placeholder="0.00" data-testid="input-cash-on-hand" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Landmark className="h-4 w-4 text-sheet" /> Bank account ($)</Label>
              <Input type="number" value={bankInput} onChange={e => setBankInput(e.target.value)} placeholder="0.00" data-testid="input-bank-balance" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Percent className="h-4 w-4 text-primary" /> Tax rate (%)</Label>
              <Input type="number" min="0" max="100" step="0.1" value={taxInput} onChange={e => setTaxInput(e.target.value)} placeholder="e.g. 25" data-testid="input-tax-rate" />
              <p className="text-xs text-muted-foreground">Used to estimate tax on your taxable profit (Expenses page).</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Landmark className="h-4 w-4 text-amber-600" /> Tax savings balance ($)</Label>
              <Input type="number" min="0" step="0.01" value={taxSavingsInput} onChange={e => setTaxSavingsInput(e.target.value)} placeholder="e.g. 2700" data-testid="input-tax-savings" />
              <p className="text-xs text-muted-foreground">Money already set aside for taxes. Not counted as profit, an expense, or a shipment distribution.</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><LifeBuoy className="h-4 w-4 text-emerald-600" /> Emergency savings opening ($)</Label>
              <Input type="number" min="0" step="0.01" value={emergencyInput} onChange={e => setEmergencyInput(e.target.value)} placeholder="e.g. 0" data-testid="input-emergency-savings" />
              <p className="text-xs text-muted-foreground">Starting emergency savings. Completed shipment allocations add on top of this.</p>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="text-sm font-medium text-muted-foreground">Total available money</span>
              <span className="font-display text-xl text-money tabular-nums" data-testid="capital-total-preview">
                ${((parseFloat(cashInput) || 0) + (parseFloat(bankInput) || 0)).toLocaleString()}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBudgetOpen(false)}>Cancel</Button>
            <Button onClick={handleBudgetSave} data-testid="save-budget-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash / Bank movement dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent data-testid="move-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider">
              {moveType === 'deposit' ? 'DEPOSIT' : moveType === 'withdraw' ? 'WITHDRAW' : 'TRANSFER'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number" min="0" step="0.01" value={moveAmount}
                onChange={e => setMoveAmount(e.target.value)} placeholder="0.00"
                data-testid="move-amount-input" autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>
                {moveType === 'deposit' ? 'Deposit into' : moveType === 'withdraw' ? 'Withdraw from' : 'Transfer from'}
              </Label>
              <Tabs value={moveBucket} onValueChange={setMoveBucket}>
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="cash" data-testid="move-bucket-cash"><Banknote className="h-4 w-4 mr-1.5 text-money" /> Cash</TabsTrigger>
                  <TabsTrigger value="bank" data-testid="move-bucket-bank"><Landmark className="h-4 w-4 mr-1.5 text-sheet" /> Bank</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {moveType === 'transfer' && (
              <p className="text-sm text-muted-foreground" data-testid="transfer-hint">
                Moving <span className="font-semibold text-foreground tabular-nums">${(parseFloat(moveAmount) || 0).toLocaleString()}</span> from{' '}
                <span className="font-semibold text-foreground">{moveBucket === 'cash' ? 'Cash' : 'Bank'}</span> to{' '}
                <span className="font-semibold text-foreground">{moveBucket === 'cash' ? 'Bank' : 'Cash'}</span>.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button onClick={handleMove} data-testid="confirm-move-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Savings Edit Balance dialog */}
      <Dialog open={savingsOpen} onOpenChange={setSavingsOpen}>
        <DialogContent data-testid="savings-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider">
              EDIT {savingsAccount === 'tax' ? 'TAX RESERVE' : 'EMERGENCY SAVINGS'} BALANCE
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-md bg-muted/50 px-3 py-2 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Current tracked balance</span>
              <span className="font-display text-xl tabular-nums" data-testid="savings-current">{money(fin?.[savingsAccount]?.balance)}</span>
            </div>
            <Tabs value={adjType} onValueChange={setAdjType}>
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="add" data-testid="adj-type-add">Add</TabsTrigger>
                <TabsTrigger value="subtract" data-testid="adj-type-subtract">Subtract</TabsTrigger>
                <TabsTrigger value="set" data-testid="adj-type-set">Set to</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="space-y-2">
              <Label>{adjType === 'set' ? 'New balance ($)' : 'Amount ($)'}</Label>
              <Input type="number" step="0.01" value={adjAmount} onChange={e => setAdjAmount(e.target.value)} placeholder="0.00" data-testid="adj-amount-input" autoFocus />
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input value={adjReason} onChange={e => setAdjReason(e.target.value)} placeholder="e.g. moved to savings account" data-testid="adj-reason-input" />
            </div>
            {adjAmount !== '' && !isNaN(parseFloat(adjAmount)) && (
              <div className="text-sm text-muted-foreground" data-testid="adj-preview">
                New balance will be{' '}
                <span className="font-semibold text-foreground tabular-nums">
                  {money(adjType === 'set' ? parseFloat(adjAmount) : adjType === 'add'
                    ? (fin?.[savingsAccount]?.balance || 0) + parseFloat(adjAmount)
                    : (fin?.[savingsAccount]?.balance || 0) - parseFloat(adjAmount))}
                </span>
              </div>
            )}
            {fin?.[savingsAccount]?.adjustments?.length > 0 && (
              <div className="border-t border-border pt-3" data-testid="adj-history">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Adjustment history</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {fin[savingsAccount].adjustments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-xs py-1 border-b border-foreground/5 last:border-0">
                      <span className="text-muted-foreground">{fmtCtDate(a.created_at)} · <span className="capitalize">{a.type}</span>{a.reason ? ` · ${a.reason}` : ''}</span>
                      <span className={`tabular-nums font-semibold ${a.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{a.amount >= 0 ? '+' : ''}{money(a.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">This records a manual adjustment to the tracked savings number only. It does not create revenue, shipment profit, or move money in your bank/cash.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavingsOpen(false)}>Cancel</Button>
            <Button onClick={handleSavingsAdjust} data-testid="save-adjustment-btn">Save Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
